import React from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Box, Typography, Button, Paper, CircularProgress, Chip } from '@mui/material';
import ArrowBack from '@mui/icons-material/ArrowBack';
import { useQuery } from '@apollo/client/react';
import { useTranslation } from 'react-i18next';
import { GetPaymentAccountDocument, type GetPaymentAccountQuery } from '@/generated/root-types';
import { subscriptionStateColors, translateSubscriptionState } from '@/utils/billingStateLabels';
import {
  detailBackButtonSx,
  detailHeaderRowWrapSx,
  detailPageContainerSx,
  detailTitleSx,
} from '@/utils/detailPageLayout';

type Account = NonNullable<GetPaymentAccountQuery['paymentAccount']>;

export default function PaymentAccountDetail() {
  const { t, i18n } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const fromTenantId = (location.state as { fromTenantId?: string })?.fromTenantId;

  const locale = i18n.language === 'de' ? 'de-DE' : 'en-US';
  const formatDate = (date?: string | null) =>
    date ? new Date(date).toLocaleDateString(locale) : '–';
  const formatCurrency = (amount?: number | null, currency?: string | null) =>
    amount != null
      ? new Intl.NumberFormat(locale, { style: 'currency', currency: currency || 'EUR' }).format(
          amount,
        )
      : '–';

  const { data, loading, error } = useQuery(GetPaymentAccountDocument, {
    variables: { id: id ?? '' },
    skip: !id,
  });

  const account = data?.paymentAccount;

  const handleBack = () => {
    if (fromTenantId) navigate(`/customers/${fromTenantId}`);
    else navigate('/customers');
  };

  const titleSecondary = (a: Account) => a.paymentMethod?.bankName || a.paymentGateway?.name || '';

  if (loading) {
    return (
      <Box
        sx={{
          ...detailPageContainerSx,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: 200,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error || !account) {
    return (
      <Box sx={detailPageContainerSx}>
        <Button startIcon={<ArrowBack />} onClick={handleBack} sx={{ mb: 2 }}>
          {t('common.back')}
        </Button>
        <Typography color="error">
          {error?.message ?? t('errors.paymentAccountNotFound')}
        </Typography>
      </Box>
    );
  }

  const gw = account.paymentGateway;
  const term = account.paymentTerm;

  return (
    <Box sx={detailPageContainerSx}>
      <Box sx={{ ...detailHeaderRowWrapSx, mb: 3 }}>
        <Button startIcon={<ArrowBack />} onClick={handleBack} sx={detailBackButtonSx}>
          {t('common.back')}
        </Button>
        <Box sx={{ flex: 1, minWidth: { xs: '100%', sm: 200 } }}>
          <Typography variant="h4" component="h1" sx={detailTitleSx}>
            {t('paymentAccountDetail.paymentAccount')}
          </Typography>
          {titleSecondary(account) && (
            <Typography variant="body1" color="text.secondary">
              {titleSecondary(account)}
            </Typography>
          )}
        </Box>
        {fromTenantId && (
          <Button size="small" onClick={() => navigate(`/customers/${fromTenantId}`)}>
            {t('common.toCustomer')}
          </Button>
        )}
      </Box>

      <Paper
        variant="outlined"
        sx={{ p: { xs: 1.5, sm: 2 }, mb: 2, width: '100%', boxSizing: 'border-box' }}
      >
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
          {t('common.gateway')}
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' },
            gap: 2,
          }}
        >
          <Box>
            <Typography variant="caption" color="text.secondary">
              {t('common.name')}
            </Typography>
            <Typography variant="body2">{gw?.name ?? '–'}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              {t('paymentAccountDetail.gatewayActive')}
            </Typography>
            <Box sx={{ mt: 0.5 }}>
              {gw ? (
                <Chip
                  label={gw.isActive ? t('common.active') : t('paymentAccountDetail.inactive')}
                  size="small"
                  color={gw.isActive ? 'success' : 'default'}
                  variant={gw.isActive ? 'filled' : 'outlined'}
                />
              ) : (
                <Typography variant="body2">–</Typography>
              )}
            </Box>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              {t('paymentAccountDetail.provider')}
            </Typography>
            <Typography variant="body2">{gw?.provider ?? '–'}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              {t('paymentAccountDetail.defaultGateway')}
            </Typography>
            <Typography variant="body2">
              {gw?.isDefault ? t('common.yes') : t('common.no')}
            </Typography>
          </Box>
        </Box>
      </Paper>

      <Paper
        variant="outlined"
        sx={{ p: { xs: 1.5, sm: 2 }, mb: 2, width: '100%', boxSizing: 'border-box' }}
      >
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
          {t('paymentAccountDetail.bankAndMandate')}
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' },
            gap: 2,
          }}
        >
          <Box>
            <Typography variant="caption" color="text.secondary">
              {t('paymentAccountDetail.bankName')}
            </Typography>
            <Typography variant="body2">{account.paymentMethod?.bankName ?? '–'}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              {t('paymentAccountDetail.accountEnding')}
            </Typography>
            <Typography variant="body2">{account.paymentMethod?.ending ?? '–'}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              {t('paymentAccountDetail.mandateId')}
            </Typography>
            <Typography
              variant="body2"
              sx={{ wordBreak: 'break-all', fontFamily: 'monospace', fontSize: '0.85rem' }}
            >
              {account.paymentMethod?.mandateId ?? '–'}
            </Typography>
          </Box>
        </Box>
      </Paper>

      <Paper
        variant="outlined"
        sx={{ p: { xs: 1.5, sm: 2 }, mb: 2, width: '100%', boxSizing: 'border-box' }}
      >
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
          {t('common.paymentTerm')}
        </Typography>
        {term ? (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' },
              gap: 2,
            }}
          >
            <Box>
              <Typography variant="caption" color="text.secondary">
                {t('common.name')}
              </Typography>
              <Typography variant="body2">{term.name}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                {t('paymentAccountDetail.termDaysLabel')}
              </Typography>
              <Typography variant="body2">
                {t('paymentAccountDetail.termDays', { count: term.days })}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                {t('paymentAccountDetail.termActive')}
              </Typography>
              <Box sx={{ mt: 0.5 }}>
                <Chip
                  label={term.isActive ? t('common.active') : t('paymentAccountDetail.inactive')}
                  size="small"
                  color={term.isActive ? 'success' : 'default'}
                  variant={term.isActive ? 'filled' : 'outlined'}
                />
              </Box>
            </Box>
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">
            –
          </Typography>
        )}
      </Paper>

      <Paper
        variant="outlined"
        sx={{ p: { xs: 1.5, sm: 2 }, mb: 2, width: '100%', boxSizing: 'border-box' }}
      >
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
          {t('paymentAccountDetail.tenant')}
        </Typography>
        <Typography variant="body2">{account.tenant?.name ?? '–'}</Typography>
      </Paper>

      <Paper
        variant="outlined"
        sx={{ p: { xs: 1.5, sm: 2 }, mb: 2, width: '100%', boxSizing: 'border-box' }}
      >
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
          {t('paymentAccountDetail.orgUnits')}
        </Typography>
        {account.organizationUnits?.length ? (
          <Box component="ul" sx={{ m: 0, pl: 2 }}>
            {account.organizationUnits.map((u) => (
              <li key={u.id}>
                <Typography variant="body2">
                  {u.name}
                  {u.description ? ` — ${u.description}` : ''}
                </Typography>
              </li>
            ))}
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">
            {t('paymentAccountDetail.noOrgUnits')}
          </Typography>
        )}
      </Paper>

      <Paper
        variant="outlined"
        sx={{ p: { xs: 1.5, sm: 2 }, mb: 2, width: '100%', boxSizing: 'border-box' }}
      >
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
          {t('paymentAccountDetail.linkedSubscriptions')}
        </Typography>
        {account.subscriptions?.length ? (
          <Box component="ul" sx={{ m: 0, pl: 0, listStyle: 'none' }}>
            {account.subscriptions.map((s) => (
              <Box key={s.id} component="li" sx={{ mb: 1.5 }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => navigate(`/subscriptions/${s.id}`, { state: { fromTenantId } })}
                  >
                    {s.number}
                  </Button>
                  <Chip
                    label={translateSubscriptionState(t, s.state)}
                    size="small"
                    color={subscriptionStateColors[s.state] || 'default'}
                  />
                  {s.amountGross != null && (
                    <Typography variant="body2" color="text.secondary">
                      {formatCurrency(s.amountGross, s.currencyCode)}
                    </Typography>
                  )}
                  <Typography variant="caption" color="text.secondary">
                    {t('common.termUntil')}: {formatDate(s.currentTermEndDate)}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">
            {t('paymentAccountDetail.noSubscriptions')}
          </Typography>
        )}
      </Paper>

      <Paper
        variant="outlined"
        sx={{ p: { xs: 1.5, sm: 2 }, mb: 2, width: '100%', boxSizing: 'border-box' }}
      >
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
          {t('organizationDetail.masterData')}
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
          <Box>
            <Typography variant="caption" color="text.secondary">
              {t('common.created')}
            </Typography>
            <Typography variant="body2">{formatDate(account.createdAt)}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              {t('common.updated')}
            </Typography>
            <Typography variant="body2">{formatDate(account.updatedAt)}</Typography>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
}
